/**
 * Benefícios ativos do jogador, do jeito que o core os publica: como TAGS no
 * `core_public_tags` do perfil da Smartico (`GetUserProfile`).
 *
 * Por que tag e não um campo próprio: o core já usa as public tags como
 * "cookie de servidor" do jogador, e ligar/desligar um benefício é escrever
 * uma tag — não precisa de release do front. O sufixo `-FRONT` é do core e
 * significa "esta tag existe para ser LIDA pelo front"; há outras tags no
 * mesmo array (segmentação, campanha) que não são benefício e devem ser
 * ignoradas em silêncio.
 *
 * O que este arquivo é: o catálogo — quais tags viram chip e em que ORDEM.
 * A ordem é de design (COINS, XP, CASHBACK), não a ordem em que o core mandou
 * o array; o array chega na ordem que a Smartico quiser.
 *
 * O que este arquivo NÃO é: fonte do nível/elo do jogador. Nível sai da régua
 * da Smartico (`useLevelsProgress`), e o tier VIP sai do `vip` de
 * `GET /players/me` (`useVipStatus`) — nenhum dos dois voltou pras tags.
 */

/** Identificador estável do benefício, para o front referenciar sem citar a tag. */
export type LevelBenefitId = 'double-coin' | 'double-xp' | 'cashback-vip';

export interface LevelBenefitTag {
  id: LevelBenefitId;
  /** A tag exata publicada pelo core no `core_public_tags`. */
  tag: string;
  /** O multiplicador, na lasca colorida do chip: "2X" ou "+". */
  prefix: string;
  /** O que é multiplicado, ao lado da lasca. */
  label: string;
  /** Traço do chip. */
  accent: string;
  /** Preenchimento da lasca — gradiente ou cor chapada. */
  capBackground: string;
  /**
   * Tinta do prefixo. Hoje é branco nos três, como o Figma escreve — a lasca é
   * pequena e o que a identifica é a COR DE FUNDO, não o glifo. Continua sendo
   * um campo por benefício (e não uma cor fixa no chip) porque quem entrar com
   * uma lasca clara vai precisar trocar só esta linha.
   */
  capInk: string;
  /** Halo da lasca (o `drop shadow` colorido do desenho). */
  capGlow: string;
}

/**
 * Catálogo dos benefícios que o front conhece. Acrescentar benefício = uma
 * linha aqui; quem consome (`useLevelBenefits`) não precisa mudar.
 *
 * A ordem daqui NÃO é a ordem da tela — quem manda na fila é o array do core
 * (ver `benefitsFromTags`). Esta lista é só o vocabulário.
 *
 * As cores vêm dos chips do Figma 22631:60024 e são de CADA BENEFÍCIO, não do
 * elo: o chip de XP é verde no Bronze e no Imortal. Quem veste a cor do elo é
 * só a barra de XP.
 */
export const LEVEL_BENEFIT_TAGS: LevelBenefitTag[] = [
  {
    id: 'double-coin',
    tag: 'DOUBLECOIN-FRONT',
    prefix: '2X',
    label: 'COINS',
    accent: '#ff704a',
    // Laranja-vermelho, não dourado: a lasca do Figma (22631:60026) é um radial
    // #FF704A → #FF3400. O dourado que estava aqui vinha do HALO da lasca, não
    // dela — e era por isso que o "2X" branco por cima sumia.
    capBackground:
      'radial-gradient(120% 130% at 32% 0%, #ff704a 0%, #ff3400 100%)',
    capInk: '#ffffff',
    capGlow: 'rgba(231, 200, 93, 0.39)',
  },
  {
    id: 'double-xp',
    tag: 'DOUBLEXP-FRONT',
    prefix: '2X',
    label: 'XP',
    accent: '#17c964',
    capBackground: '#22c55e',
    capInk: '#ffffff',
    capGlow: 'rgba(43, 255, 121, 0.58)',
  },
  {
    id: 'cashback-vip',
    tag: 'CASHBACKVIP-FRONT',
    prefix: '+',
    label: 'CASHBACK',
    accent: '#c57dfd',
    capBackground: 'linear-gradient(90deg, #a32bff 0%, #ca84ff 100%)',
    capInk: '#ffffff',
    capGlow: '#a32bff',
  },
];

/**
 * Normaliza a tag antes de comparar. O contrato não é versionado por ninguém —
 * a mesma dor que tirou o tier VIP das tags —, então caixa e espaço em volta
 * não podem decidir se o jogador vê o benefício que já foi concedido a ele.
 */
function normalizeTag(tag: string) {
  return tag.trim().toUpperCase();
}

/** Vocabulário indexado pela tag normalizada, pra tradução em tempo constante. */
const BENEFIT_BY_TAG = new Map(
  LEVEL_BENEFIT_TAGS.map((benefit) => [normalizeTag(benefit.tag), benefit]),
);

/**
 * Quais benefícios estão ligados para este jogador, NA ORDEM EM QUE O CORE
 * MANDOU. A fila é do core de propósito: é ele quem sabe o que quer em
 * primeiro lugar, e ordenar por uma lista local aqui faria a pílula pular de
 * posição sem ninguém ter mudado nada.
 *
 * A fila também sai COMPACTA: quem percorre é o array do core, então tag
 * desconhecida (`VIP_0`, segmentação, campanha) e benefício desligado não
 * deixam vão nenhum — as pílulas que sobram ficam encostadas. Tag repetida
 * entra uma vez só.
 *
 * Array ausente (perfil ainda não chegou) resulta em lista vazia — nunca em
 * erro.
 */
export function benefitsFromTags(tags?: string[] | null): LevelBenefitTag[] {
  if (!Array.isArray(tags) || !tags.length) return [];

  const seen = new Set<LevelBenefitId>();
  const benefits: LevelBenefitTag[] = [];

  for (const tag of tags) {
    if (typeof tag !== 'string') continue;

    const benefit = BENEFIT_BY_TAG.get(normalizeTag(tag));

    if (!benefit || seen.has(benefit.id)) continue;

    seen.add(benefit.id);
    benefits.push(benefit);
  }

  return benefits;
}
